import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Typography,
  Box
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import { sampleExcel } from "../utils/excelUtils";
import { bulkUpload } from "../services/physicalMeasurementsApi";

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ImportDialog: React.FC<ImportDialogProps> = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccessMsg(null);
      onClose();
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset feedback states
    setError(null);
    setSuccessMsg(null);

    // Validate file extension
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
      setError("Please select a valid Excel file (.xlsx or .xls).");
      e.target.value = "";
      return;
    }

    setLoading(true);
    try {
      const response = await bulkUpload(file);
      const resData = response.data;
      let message = "File uploaded successfully!";

      if (typeof resData === "object" && resData !== null) {
        if (resData.message) {
          message = resData.message;
        } else if (resData.savedCount !== undefined) {
          message = `Import complete: ${resData.savedCount} record(s) saved, ${resData.duplicateCount || 0} duplicate(s) skipped.`;
        }
      } else if (typeof resData === "string") {
        message = resData;
      }

      setSuccessMsg(message);

      if (onSuccess) {
        onSuccess();
      }

      // Automatically close after a short delay so user sees success
      setTimeout(() => {
        handleClose();
      }, 2500);
    } catch (err: any) {
      console.error("Bulk upload failed:", err);
      let errorDetail = "Failed to upload file. Please check the Excel format and try again.";
      if (err.response?.data) {
        if (typeof err.response.data === "string") {
          errorDetail = err.response.data;
        } else if (err.response.data.message) {
          errorDetail = err.response.data.message;
        }
      } else if (err.message) {
        errorDetail = err.message;
      }
      setError(errorDetail);
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle component="div" sx={{ fontWeight: 600, color: "#1F497D" }}>
        Import Physical Measurements
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Download the sample template, fill in your student and measurement data, and upload the Excel file (.xlsx / .xls).
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {successMsg && (
            <Alert severity="success">
              {successMsg}
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={sampleExcel}
              disabled={loading}
              sx={{
                borderColor: "#1F497D",
                color: "#1F497D",
                "&:hover": { borderColor: "#163A63" }
              }}
            >
              Download Sample Excel
            </Button>

            <Button
              variant="contained"
              component="label"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
              disabled={loading}
              sx={{
                backgroundColor: "#1F497D",
                "&:hover": { backgroundColor: "#163A63" }
              }}
            >
              {loading ? "Uploading..." : "Bulk Upload"}
              <input
                type="file"
                hidden
                accept=".xlsx, .xls"
                onChange={handleUpload}
                disabled={loading}
              />
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
